import { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ClipboardCheck, Library, Search, Loader2, Save, X, Sparkles, Settings2,
  Paperclip, ChevronRight, Trash2, Wand2,
} from 'lucide-react';

const STATUS_COLORS = {
  Pending:      'bg-slate-100 text-slate-700 border-slate-200',
  'In Progress':'bg-amber-100 text-amber-800 border-amber-200',
  Fulfilled:    'bg-emerald-100 text-emerald-800 border-emerald-200',
  Waived:       'bg-slate-50 text-slate-500 border-slate-200',
};
const REVIEW_COLORS = {
  'Pending Review': 'bg-slate-100 text-slate-700 border-slate-200',
  Approved:         'bg-emerald-100 text-emerald-800 border-emerald-200',
  Rejected:         'bg-red-100 text-red-800 border-red-200',
};
const STATUSES = ['Pending', 'In Progress', 'Fulfilled', 'Waived'];
const REVIEWS = ['Pending Review', 'Approved', 'Rejected'];

function StatCard({ label, value, tone = 'slate', testid }) {
  const t = { slate: 'text-slate-900', emerald: 'text-emerald-600', amber: 'text-amber-600', red: 'text-red-600', blue: 'text-blue-600' };
  return (
    <Card className="border-slate-200"><CardContent className="py-3 px-4">
      <div className={`text-2xl font-bold ${t[tone]}`} data-testid={testid}>{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </CardContent></Card>
  );
}

function EditDialog({ open, onOpenChange, req, canWrite, onSaved }) {
  const [state, setState] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!req) return;
    setState({
      requested_evidence: req.requested_evidence || '',
      requested_from: req.requested_from || '',
      status: req.status || 'Pending',
      review_status: req.review_status || 'Pending Review',
      review_notes: req.review_notes || '',
    });
  }, [req]);
  if (!req) return null;

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/evidence-engine/requests/${req.control_id}`, state);
      toast.success('Evidence request saved');
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const dismiss = async () => {
    if (!window.confirm('Delete this evidence request?')) return;
    try {
      await axios.delete(`${API}/evidence-engine/requests/${req.control_id}`);
      toast.success('Evidence request removed');
      onSaved?.({ ...req, is_deleted: true });
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="evidence-edit-dialog" className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="font-mono text-slate-500 text-base">{req.control_id}</span>
            <span>{req.control_name}</span>
          </DialogTitle>
          <DialogDescription asChild>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {!req.is_user_modified && <Badge className="bg-blue-50 text-blue-700 border-blue-200 border text-[10px]"><Sparkles className="w-3 h-3 mr-1" /> SYSTEM GENERATED</Badge>}
              {req.is_user_modified && <Badge className="bg-purple-100 text-purple-800 border-purple-200 border text-[10px]">USER MODIFIED</Badge>}
              <Badge className={`${STATUS_COLORS[req.status]} border`}>{req.status}</Badge>
              <Badge className={`${REVIEW_COLORS[req.review_status]} border`}>{req.review_status}</Badge>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
          <strong>Evidence uploaded once → reused everywhere.</strong> Files attached to this Master Control&apos;s
          assessment are automatically counted for all mapped framework requirements below.
        </div>

        {req.affected_frameworks?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {req.affected_frameworks.map((f) => (
              <span key={f.framework.id}
                className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium border"
                style={{ borderColor: f.framework.color, color: f.framework.color, backgroundColor: `${f.framework.color}14` }}
                title={(f.codes || []).join(', ')}
              >
                {f.framework.short_name} · {(f.codes || []).join(', ')}
              </span>
            ))}
          </div>
        )}

        <div className="mt-4 space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase">Requested evidence</label>
            <Textarea rows={3} value={state.requested_evidence}
              onChange={(e) => setState({ ...state, requested_evidence: e.target.value })}
              disabled={!canWrite} data-testid="evidence-req-text" className="mt-1" />
            {req.evidence_examples && (
              <p className="text-xs text-slate-500 mt-1"><strong>Examples:</strong> {req.evidence_examples}</p>
            )}
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase">Requested from</label>
              <Input value={state.requested_from}
                onChange={(e) => setState({ ...state, requested_from: e.target.value })}
                disabled={!canWrite} data-testid="evidence-requested-from" className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase">Status</label>
              <Select value={state.status} onValueChange={(v) => setState({ ...state, status: v })} disabled={!canWrite}>
                <SelectTrigger data-testid="evidence-status-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase">Review status</label>
              <Select value={state.review_status} onValueChange={(v) => setState({ ...state, review_status: v })} disabled={!canWrite}>
                <SelectTrigger data-testid="evidence-review-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{REVIEWS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase">Review notes</label>
            <Textarea rows={2} value={state.review_notes}
              onChange={(e) => setState({ ...state, review_notes: e.target.value })}
              disabled={!canWrite} data-testid="evidence-review-notes" className="mt-1" />
          </div>
        </div>

        <section className="mt-4 rounded-lg border border-slate-200 bg-white p-3">
          <h3 className="text-xs font-semibold text-slate-600 uppercase mb-2 flex items-center gap-1">
            <Paperclip className="w-3 h-3" /> Received evidence ({req.received_count})
          </h3>
          {req.received_count === 0 ? (
            <p className="text-sm text-slate-500 italic">
              No files attached yet. Upload evidence via the master control assessment ({req.control_id}) — it will appear here automatically.
            </p>
          ) : (
            <ul className="space-y-1 text-sm">
              {req.received_evidence.map((f) => (
                <li key={f.document_id} data-testid={`evidence-file-${f.document_id}`} className="flex items-center justify-between text-xs">
                  <div>
                    <span className="font-medium text-slate-800">{f.file_name}</span>
                    <span className="text-slate-500 ml-2">{f.document_type} · {(f.file_size / 1024).toFixed(1)} KB</span>
                    {f.description && <div className="text-slate-500">{f.description}</div>}
                  </div>
                  <span className="text-slate-400">{(f.uploaded_at || '').slice(0, 10)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <div className="flex justify-between mt-4">
          <Button variant="ghost" onClick={dismiss} disabled={!canWrite} data-testid="evidence-delete-btn">
            <Trash2 className="w-4 h-4 mr-1 text-red-500" /> Remove request
          </Button>
          <Button onClick={save} disabled={saving || !canWrite} data-testid="evidence-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function EvidenceEnginePage() {
  const { user } = useContext(AuthContext);
  const canWrite = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'client' || user?.role === 'auditor';
  const canSettings = user?.role === 'admin' || user?.role === 'super_admin';
  const [enabled, setEnabled] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [reviewFilter, setReviewFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (reviewFilter !== 'all') params.set('review_status', reviewFilter);
      const r = await axios.get(`${API}/evidence-engine/requests?${params.toString()}`);
      setData(r.data);
      setEnabled(r.data.feature_enabled !== false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, [search, statusFilter, reviewFilter]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/evidence-engine/requests/generate`);
      toast.success(`Generated ${r.data.created} evidence requests (skipped ${r.data.skipped_existing} existing).`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Generate failed'); }
    finally { setGenerating(false); }
  };

  const toggleFlag = async (v) => {
    try {
      await axios.patch(`${API}/evidence-engine/settings`, { use_new_evidence_engine: v });
      setEnabled(v);
      toast.success(`Evidence Engine ${v ? 'enabled' : 'disabled'}`);
      load();
      setSettingsOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Toggle failed'); }
  };

  const s = data?.summary || {};

  if (data && data.feature_enabled === false) {
    return (
      <div className="space-y-4" data-testid="evidence-engine-page">
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
          <ClipboardCheck className="w-7 h-7 text-slate-700" /> Evidence Engine
        </h1>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 text-sm text-amber-900 flex items-center justify-between">
            <span><strong>New Evidence Engine is OFF.</strong> Enable it in Settings.</span>
            {canSettings && (
              <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="evidence-settings-btn">
                <Settings2 className="w-4 h-4 mr-1" /> Settings
              </Button>
            )}
          </CardContent>
        </Card>
        <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} enabled={enabled} onToggle={toggleFlag} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="evidence-engine-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Library className="w-4 h-4" /><span>Compliance Engine v2</span>
            <span className="text-slate-300">/</span><span>Evidence Engine</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <ClipboardCheck className="w-7 h-7 text-slate-700" /> Evidence Automation
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            One evidence file, all mapped frameworks. Auto-generated requests drive collection; uploads land on the Master Control assessment and are reused everywhere.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canWrite && (
            <Button onClick={generate} disabled={generating} data-testid="evidence-generate-btn">
              {generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />}
              Auto-generate requests
            </Button>
          )}
          {canSettings && (
            <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="evidence-settings-btn">
              <Settings2 className="w-4 h-4 mr-1" /> Settings
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard label="Total requests" value={s.total ?? 0} testid="evidence-total" />
        <StatCard label="Pending" value={s.pending ?? 0} tone="amber" testid="evidence-pending" />
        <StatCard label="Fulfilled" value={s.fulfilled ?? 0} tone="emerald" testid="evidence-fulfilled" />
        <StatCard label="Waived" value={s.waived ?? 0} testid="evidence-waived" />
        <StatCard label="Approved" value={s.approved ?? 0} tone="emerald" testid="evidence-approved" />
        <StatCard label="With files" value={s.with_files ?? 0} tone="blue" testid="evidence-with-files" />
      </div>

      <Card className="border-slate-200"><CardContent className="p-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by control, name, requested from…" className="pl-9" data-testid="evidence-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="evidence-filter-status" className="w-[160px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={reviewFilter} onValueChange={setReviewFilter}>
          <SelectTrigger data-testid="evidence-filter-review" className="w-[180px]"><SelectValue placeholder="Review" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All reviews</SelectItem>
            {REVIEWS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        {(search || statusFilter !== 'all' || reviewFilter !== 'all') && (
          <Button variant="ghost" onClick={() => { setSearch(''); setStatusFilter('all'); setReviewFilter('all'); }} data-testid="evidence-clear-filters">
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        )}
      </CardContent></Card>

      <Card className="border-slate-200"><CardContent className="p-0"><div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-[90px]">Control</TableHead>
            <TableHead>Requested evidence</TableHead>
            <TableHead>Requested from</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Review</TableHead>
            <TableHead className="text-center">Files</TableHead>
            <TableHead>Frameworks</TableHead>
            <TableHead className="w-[40px]"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</TableCell></TableRow>
            ) : (data?.requests || []).length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-16 text-slate-500">
                No evidence requests. Click <strong>Auto-generate requests</strong> above to seed them from the Master Control Library.
              </TableCell></TableRow>
            ) : (
              data.requests.map((r) => (
                <TableRow key={r.control_id} onClick={() => { setSelected(r); setDialogOpen(true); }}
                  className="cursor-pointer hover:bg-slate-50" data-testid={`evidence-row-${r.control_id}`}>
                  <TableCell className="font-mono text-slate-700">
                    {r.control_id}
                    <div className="text-[10px] text-slate-500 line-clamp-1">{r.control_name}</div>
                  </TableCell>
                  <TableCell className="text-xs text-slate-700 max-w-md">
                    <div className="line-clamp-2">{r.requested_evidence}</div>
                  </TableCell>
                  <TableCell className="text-xs text-slate-700">{r.requested_from || <span className="text-slate-400 italic">Unassigned</span>}</TableCell>
                  <TableCell><Badge className={`${STATUS_COLORS[r.status]} border text-xs`}>{r.status}</Badge></TableCell>
                  <TableCell><Badge className={`${REVIEW_COLORS[r.review_status]} border text-xs`}>{r.review_status}</Badge></TableCell>
                  <TableCell className="text-center">
                    {r.received_count > 0 ? (
                      <Badge className="bg-blue-50 text-blue-700 border-blue-200 border text-xs">
                        <Paperclip className="w-3 h-3 mr-1" />{r.received_count}
                      </Badge>
                    ) : <span className="text-slate-300 text-xs">—</span>}
                  </TableCell>
                  <TableCell><div className="flex flex-wrap gap-1">
                    {(r.affected_frameworks || []).map((f) => (
                      <span key={f.framework.id} className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium border"
                        style={{ borderColor: f.framework.color, color: f.framework.color, backgroundColor: `${f.framework.color}14` }}>
                        {f.framework.short_name}
                      </span>
                    ))}
                  </div></TableCell>
                  <TableCell><ChevronRight className="w-4 h-4 text-slate-400" /></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div></CardContent></Card>

      <EditDialog open={dialogOpen} onOpenChange={setDialogOpen} req={selected} canWrite={canWrite} onSaved={load} />
      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} enabled={enabled} onToggle={toggleFlag} />
    </div>
  );
}

function SettingsDialog({ open, onOpenChange, enabled, onToggle }) {
  const [checked, setChecked] = useState(enabled);
  useEffect(() => { setChecked(enabled); }, [enabled]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="evidence-settings-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle>Evidence Engine settings</DialogTitle></DialogHeader>
        <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3 mt-3">
          <div className="pr-4">
            <div className="text-sm font-medium text-slate-900">Use New Evidence Engine</div>
            <div className="text-xs text-slate-500 mt-1">Auto-generate evidence requests from Master Controls. Files stay on the assessment and are reused across every mapped framework.</div>
          </div>
          <Switch checked={checked} onCheckedChange={setChecked} data-testid="evidence-engine-toggle" />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onToggle(checked)} data-testid="evidence-settings-save-btn">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
